import { withTranslation, WithTranslation } from 'react-i18next';
import React, { ChangeEvent, useEffect, useState } from 'react';
import i18n from 'i18next';
import getOrganizationTypes from '../../../../helpers/organizationTypes';
import InputField from '../../../form/inputField';
import DropDownField from '../../../form/dropdownField';
import PopoverContainer from '../../../popover/popover';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import { ContactFormResultKeys } from '../contactFormModel';
import { EAccountType, IAccountModel } from '../../../../helpers/models/accountModel';
import getProvinces from '../../../../helpers/provinces';
import getRoles from '../../../../helpers/role';
import { ReactComponent as NotificationLogo } from '../../../../styles/img/icons/error.svg';
import { handleInputWithLabelOnBlur, handleInputWithLabelOnFocus } from '../../registration';
import {
    FORM_FIRSTNAME_MAX_LENGTH,
    FORM_LASTNAME_MAX_LENGTH,
    FORM_PHONE_EXT_MAX_LENGTH,
    FORM_PHONE_MAX_LENGTH,
    validatePhoneNumberFormat,
} from '../../utils/formValidation';

export enum EPracticeType {
    // eslint-disable-next-line no-unused-vars
    ORGANIZATION = 'registration_contact_organization',
    // eslint-disable-next-line no-unused-vars
    PROFESSIONAL = 'registration_contact_professional',
}

export interface IContactProps extends WithTranslation {
    readonly?: boolean;
    account?: IAccountModel;

    provinces?: IProvinceModel[];
    roles?: IRoleModel[];
}

const Contact = (props: IContactProps) => {
    const { t, readonly, account } = props;

    const [provinces, setProvinces] = useState<IProvinceModel[]>(props.provinces ?? []);
    const [roles, setRoles] = useState<IRoleModel[]>(props.roles ?? []);
    const [practice, setPractice] = useState<EPracticeType | null>();
    const [organizationTypes, setOrganizationTypes] = useState<IRoleModel[]>([]);

    const roleComponents = roles?.map((item: IRoleModel) => {
        return (
            <option key={item.id} value={item.id}>
                {item.name}
            </option>
        );
    });

    const provinceComponents = provinces?.map((item: IProvinceModel) => {
        return (
            <option key={item.id} value={item.id}>
                {item.name}
            </option>
        );
    });

    useEffect(() => {
        async function getData() {
            const provincesList = await getProvinces(i18n.language);
            setProvinces(provincesList ?? []);

            const roleList = await getRoles(i18n.language);
            setRoles(roleList ?? []);

            const organizationType = await getOrganizationTypes(i18n.language);
            setOrganizationTypes(organizationType ?? []);
        }
        getData();
    }, [i18n.language]);

    useEffect(() => {
        if (account) {
            setPractice(
                account?.type === EAccountType.ORGANIZATION
                    ? EPracticeType.ORGANIZATION
                    : EPracticeType.PROFESSIONAL
            );
            document.getElementById('registration_specialist_group')!.style.display =
                account.type === EAccountType.PROFESSIONAL ? 'unset' : 'none';
            document.getElementById('registration_organization_type')!.style.display =
                account.type === EAccountType.ORGANIZATION ? 'flex' : 'none';
        }
    }, [account]);

    const organizationTypesComponent = organizationTypes.map((item: IRoleModel) => {
        return (
            <option key={item.id} value={item.id}>
                {item.name}
            </option>
        );
    });

    const handleChangePracticeType = (e: ChangeEvent<HTMLInputElement>) => {
        document.getElementById('registration_specialist_group')!.style.display =
            e.target.value === EPracticeType.PROFESSIONAL ? 'unset' : 'none';
        document.getElementById('registration_organization_type')!.style.display =
            e.target.value === EPracticeType.ORGANIZATION ? 'flex' : 'none';
        document.getElementById('registration-radio-button-error')!.style.display = 'none';
        document.getElementById(EPracticeType.ORGANIZATION)!.classList.remove('registration-error');
        document.getElementById(EPracticeType.PROFESSIONAL)!.classList.remove('registration-error');
    };

    return (
        <div className="registration-form-container">
            <div className="registration-section">
                <h3 className="header-3 header-3--purple registration-contact-title">
                    {t('form_contact_info_title')}
                </h3>
                <InputField
                    fieldContainerClassName="registration-field"
                    fieldName={ContactFormResultKeys.FIRSTNAME}
                    fieldTitle="form_contact_info_first_name"
                    fieldRequired
                    type="text"
                    defaultValue={account ? account?.firstname ?? '' : ''}
                    fieldWeight={0}
                    maxLength={FORM_FIRSTNAME_MAX_LENGTH}
                    fieldDisabled={readonly}
                    fieldHiddenLabel="form_contact_info_title"
                />
                <InputField
                    fieldContainerClassName="registration-field"
                    fieldName={ContactFormResultKeys.LASTNAME}
                    fieldTitle="form_contact_info_last_name"
                    fieldRequired
                    type="text"
                    defaultValue={account ? account?.lastname ?? '' : ''}
                    fieldWeight={1}
                    maxLength={FORM_LASTNAME_MAX_LENGTH}
                    fieldDisabled={readonly}
                    fieldHiddenLabel="form_contact_info_title"
                />
                <div className="registration-column-container">
                    <InputField
                        fieldContainerClassName="registration-field registration-contact-info-phone-field registration-column-item"
                        fieldName={ContactFormResultKeys.PHONE}
                        fieldTitle="form_contact_info_phone"
                        fieldRequired
                        type="text"
                        defaultValue={account ? account?.phone ?? '' : ''}
                        placeholder="999-999-9999"
                        fieldWeight={2}
                        inputMode="numeric"
                        maxLength={FORM_PHONE_MAX_LENGTH}
                        handleOnChange={validatePhoneNumberFormat}
                        fieldDisabled={readonly}
                        fieldHiddenLabel="form_contact_info_title"
                    />
                    <InputField
                        fieldContainerClassName="registration-field registration-column-item registration-contact-info-ext-field"
                        fieldName={ContactFormResultKeys.PHONE_EXT}
                        fieldTitle="form_contact_info_phone_ext"
                        fieldOptionalText="form_contact_info_phone_ext_option"
                        defaultValue={account ? account?.ext ?? '' : ''}
                        fieldWeight={3}
                        inputMode="numeric"
                        maxLength={FORM_PHONE_EXT_MAX_LENGTH}
                        handleOnChange={validatePhoneNumberFormat}
                        fieldDisabled={readonly}
                        fieldHiddenLabel="form_contact_info_title"
                    />
                </div>
                <InputField
                    fieldDisabled={readonly}
                    fieldContainerClassName="registration-field"
                    fieldName={ContactFormResultKeys.EMAIL}
                    fieldTitle="form_contact_info_email"
                    fieldRequired
                    fieldHiddenLabel="form_contact_info_title"
                    fieldTooltip={
                        <PopoverContainer
                            icon
                            className={'field-tooltip-icon'}
                            prefix={t('form_contact_info_email')}
                        >
                            <div>
                                <p className="body-medium">
                                    {t('form_contact_popover_info_email')}
                                </p>
                            </div>
                        </PopoverContainer>
                    }
                    type="email"
                    defaultValue={account ? account?.email ?? '' : ''}
                    fieldWeight={4}
                />
                {!readonly && (
                    <InputField
                        fieldContainerClassName="registration-field"
                        fieldName={ContactFormResultKeys.EMAIL_CONFIRMATION}
                        fieldTitle="form_contact_info_email_confirm"
                        type="email"
                        fieldWeight={5}
                        fieldHiddenLabel="form_contact_info_title"
                    />
                )}
            </div>
            <div className="registration-section">
                <h3 className="header-3 header-3--purple registration-contact-title">
                    {t('form_contact_info_practice_title')}
                </h3>
                <div className="radio-button-container">
                    <input
                        className="radio-button--input field-value"
                        type="radio"
                        disabled={readonly}
                        id={EPracticeType.PROFESSIONAL}
                        name={ContactFormResultKeys.PRACTICE}
                        value={EPracticeType.PROFESSIONAL}
                        checked={practice === EPracticeType.PROFESSIONAL}
                        onChange={(e) => {
                            handleChangePracticeType(e);
                            setPractice(EPracticeType.PROFESSIONAL);
                        }}
                        onFocus={handleInputWithLabelOnFocus}
                        onBlur={handleInputWithLabelOnBlur}
                        data-set-weight={6}
                        aria-required
                    />
                    <div className="radio-button">
                        <div
                            className={'radio-button field-label registration-field '.concat(
                                readonly ? 'field-label--disabled' : ''
                            )}
                        >
                            <div className="registration_contact_info_practice_type_container">
                                <div>
                                    <label htmlFor={EPracticeType.PROFESSIONAL}>
                                        <div className="accessibility-hidden">
                                            {t('form_contact_info_practice_title')}
                                        </div>
                                        {t('form_contact_practice_professional')}{' '}
                                    </label>
                                    <PopoverContainer
                                        icon
                                        className={'field-tooltip-icon'}
                                        prefix={t('form_contact_info_practice_title').addSpace().concat(t('form_contact_practice_professional'))}
                                    >
                                        <>
                                            <p className={'body-medium'}>
                                                {t(
                                                    'form_contact_popover_practice_independent_option'
                                                )}
                                            </p>
                                            <ul>
                                                <li className={'body-medium'}>
                                                    {t(
                                                        'form_contact_popover_practice_independent_option_practitioner'
                                                    )}
                                                </li>
                                                <li className={'body-medium'}>
                                                    {t(
                                                        'form_contact_popover_practice_independent_option_location'
                                                    )}
                                                </li>
                                                <li className={'body-medium'}>
                                                    {t(
                                                        'form_contact_popover_practice_independent_option_proprietor'
                                                    )}
                                                </li>
                                                <li className={'body-medium'}>
                                                    {t(
                                                        'form_contact_popover_practice_independent_option_business_id'
                                                    )}
                                                </li>
                                            </ul>
                                            <p className={'body-medium'}>
                                                {t(
                                                    'form_contact_popover_practice_independent_option_optician'
                                                )}
                                            </p>
                                        </>
                                    </PopoverContainer>
                                </div>
                                <div className="body-medium">
                                    {t('form_contact_practice_professional_description')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="radio-button-container">
                    <input
                        className="radio-button--input field-value"
                        type="radio"
                        disabled={readonly}
                        id={EPracticeType.ORGANIZATION}
                        name={ContactFormResultKeys.PRACTICE}
                        value={EPracticeType.ORGANIZATION}
                        checked={practice === EPracticeType.ORGANIZATION}
                        onChange={(e) => {
                            handleChangePracticeType(e);
                            setPractice(EPracticeType.ORGANIZATION);
                        }}
                        onFocus={handleInputWithLabelOnFocus}
                        onBlur={handleInputWithLabelOnBlur}
                        data-set-weight={6}
                        aria-required
                    />
                    <div className="radio-button">
                        <div
                            className={'radio-button field-label registration-field '.concat(
                                readonly ? 'field-label--disabled' : ''
                            )}
                        >
                            <div className="registration_contact_info_practice_type_container">
                                <div>
                                    <label htmlFor={EPracticeType.ORGANIZATION}>
                                        <div className="accessibility-hidden">
                                            {t('form_contact_info_practice_title')}
                                        </div>
                                        {t('form_contact_practice_organization')}{' '}
                                    </label>
                                    <PopoverContainer
                                        icon
                                        className={'field-tooltip-icon'}
                                        prefix={t('form_contact_info_practice_title').addSpace().concat(t('form_contact_practice_organization'))}
                                    >
                                        <>
                                            <p className="body-medium">
                                                {t(
                                                    'form_contact_popover_practice_organization_option'
                                                )}
                                            </p>
                                            <ul>
                                                <li className="body-medium">
                                                    {t(
                                                        'form_contact_popover_practice_organization_option_providers'
                                                    )}
                                                </li>
                                                <li className="body-medium">
                                                    {t(
                                                        'form_contact_popover_practice_organization_option_cra'
                                                    )}
                                                </li>
                                                <li className="body-medium">
                                                    {t(
                                                        'form_contact_popover_practice_organization_option_bank'
                                                    )}
                                                </li>
                                                <li className="body-medium">
                                                    {t(
                                                        'form_contact_popover_practice_organization_option_locations'
                                                    )}
                                                </li>
                                            </ul>
                                            <p className="body-medium">
                                                {t(
                                                    'form_contact_popover_practice_organization_option_opticians'
                                                )}
                                            </p>
                                        </>
                                    </PopoverContainer>
                                </div>
                                <div className="body-medium">
                                    {t('form_contact_practice_organization_description')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="registration-radio-button-error" className="radio-button-error">
                    <NotificationLogo className="radio-button-error-icon" />
                    <span className="field-value radio-button-error-value registration-error">
                        Required
                    </span>
                </div>
                <div className="registration_contact_practice_info_container">
                    <DropDownField
                        fieldContainerClassName="field-container--large registration-column-item"
                        fieldName={ContactFormResultKeys.PROVINCE}
                        fieldTitle="form_contact_practice_state"
                        fieldOptions={provinceComponents}
                        fieldDefaultValue="form_contact_practice_state_default"
                        value={account ? (account?.province as IProvinceModel).id : ''}
                        fieldDisabled={readonly}
                        fieldRequired
                        fieldWeight={7}
                        fieldHiddenLabel="form_contact_info_title"
                    />
                    <DropDownField
                        fieldId="registration_specialist_group"
                        fieldContainerClassName="field-container--large registration-column-item"
                        fieldName={ContactFormResultKeys.SPECIALIST_GROUP}
                        fieldTitle="form_contact_practice_specialist_group"
                        fieldOptions={roleComponents}
                        fieldDefaultValue="form_contact_practice_specialist_group_default"
                        value={
                            account && account.type === EAccountType.PROFESSIONAL
                                ? (account?.role as IRoleModel).id ?? ''
                                : ''
                        }
                        fieldDisabled={readonly}
                        fieldWeight={8}
                        aria-required
                        fieldHiddenLabel="form_contact_info_title"
                    />
                    <DropDownField
                        fieldId="registration_organization_type"
                        fieldContainerClassName="field-container--large registration-column-item"
                        fieldName={ContactFormResultKeys.ORGANIZATION_TYPE}
                        fieldTitle="form_contact_practice_organization_type"
                        fieldOptions={organizationTypesComponent}
                        fieldDefaultValue="form_contact_practice_organization_type_default"
                        value={
                            account && account.type === EAccountType.ORGANIZATION
                                ? (account?.role as IRoleModel).id ?? ''
                                : ''
                        }
                        fieldDisabled={readonly}
                        fieldWeight={8}
                        fieldHiddenLabel="form_contact_info_title"
                        aria-required
                    />
                </div>
            </div>
        </div>
    );
};

export default withTranslation()(Contact);
